import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dynClass } from "trader.ts/ui";
import { textbox } from "trader.ts/ui.common";
import { Form } from "trader.ts/ui.forms";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderPopupModal } from "../../client/popup";
import {
  radioField,
  radioFieldCheckboxRenderInputLike,
} from "../../client/radiofield";
import { renderSaveButton } from "../../client/savebutton";

export function renderEditor<T = null>(
  htmlS: Source<string>,
  textS: null | Source<string>,
  variables: null | {
    records: T[];
    variables: {
      technicalname: string;
      description: string;
      extract: (t: T) => string;
    }[];
  },
  opts?: { style?: string; className?: string },
): { buttonSection: HTMLElement; editablediv: HTMLElement } {
  const isBold = new Source(false);
  const isItalic = new Source(false);
  const isUnderlined = new Source(false);
  const inputtingLinkS = new Source(false);
  const insertingVariableS = new Source(false);

  const selectionS: Source<Range[] | null> = new Source(null);
  const linkInput = new Source("");

  const editablediv = (
    <div
      style={opts?.style || ""}
      className={opts?.className ?? style.editor_window}
      oninput={handleInput}
      onkeydown={handleKeydown}
      onclick={checkActiveStates}
    ></div>
  );
  editablediv.setAttribute("contenteditable", "true");
  editablediv.innerHTML = htmlS.get();
  editablediv.addEventListener("paste", function () {
    setTimeout(() => {
      editablediv
        .querySelectorAll("*")
        .forEach((el) => el.removeAttribute("style"));
    }, 10);
  });

  function handleInput() {
    // If any textcontent is in the editablediv itself, it gets lost
    // so we wrap it in a div, shouldn't be a problem
    htmlS.set("<div>" + editablediv.innerHTML + "</div>");
    textS?.set(editablediv.innerText);
  }

  function checkActiveStates() {
    setTimeout(function () {
      /* eslint-disable */
      isBold.set(document.queryCommandState("bold"));
      isItalic.set(document.queryCommandState("italic"));
      isUnderlined.set(document.queryCommandState("underline"));
      /* eslint-enable */
    }, 1);
  }

  function handleKeydown(ev: KeyboardEvent) {
    /* eslint-disable */
    if (ev.keyCode === 9) {
      ev.preventDefault();
    } else {
      if (editablediv.innerHTML === "" || editablediv.innerHTML === "<br>") {
        document.execCommand("formatBlock", false, "<div>");
      }
      /* eslint-enable */

      checkActiveStates();
    }
  }

  function apply(command: string) {
    editablediv.focus();
    /* eslint-disable */
    document.execCommand(command, false);
    /* eslint-enable */
    checkActiveStates();
  }

  function format(tag: string) {
    editablediv.focus();
    /* eslint-disable */
    document.execCommand("formatBlock", false, tag);
    /* eslint-enable */
    checkActiveStates();
  }

  function startMakingLink() {
    const selection = saveSelection();
    if (selection !== null && isValidSelection(selection)) {
      inputtingLinkS.set(true);
      selectionS.set(selection);
    } else {
      alert("Selecteer eerst tekst om een link te maken");
    }
  }

  function saveLink() {
    const selection = selectionS.get();
    if (selection === null) {
      return;
    } else {
      const inp = linkInput.get();
      restoreSelection(selection);
      selectionS.set(null);
      inputtingLinkS.set(false);
      linkInput.set("");

      /* eslint-disable */
      document.execCommand("createLink", false, inp);
      /* eslint-enable */

      editablediv
        .querySelectorAll("a")
        .forEach((a) => a.setAttribute("target", "_blank"));
      handleInput();
    }
  }

  function startInsertingVariable() {
    const selection = saveSelection();
    if (selection === null) {
      alert(
        "Zet je cursor eerst in het tekstveld om een variabele in te voegen",
      );
    } else {
      selectionS.set(selection);
      insertingVariableS.set(true);
    }
  }
  function insertVariable(variable: { technicalname: string }) {
    const selection = selectionS.get();
    editablediv.focus();
    if (selection === null) {
    } else {
      restoreSelection(selection);
      setTimeout(() => {
        const textToInsert = "{{" + variable.technicalname + "}}";
        /* eslint-disable */
        document.execCommand("insertText", false, textToInsert);
        /* eslint-enable */
        checkActiveStates();
      }, 10);
    }
  }

  function activatableClass(s: Source<boolean>, html: HTMLElement) {
    return dynClass(
      s,
      (val) =>
        val === true
          ? style.editor_control_button + style.editor_control_button_active
          : style.editor_control_button,
      html,
    );
  }

  const buttonSection = (
    <div className={style.editor_control_buttons}>
      {activatableClass(
        isBold,
        <button onclick={() => apply("bold")}>
          <b>B</b>
        </button>,
      )}
      {activatableClass(
        isItalic,
        <button onclick={() => apply("italic")}>
          <i>I</i>
        </button>,
      )}
      {activatableClass(
        isUnderlined,
        <button onclick={() => apply("underline")}>
          <span style="text-decoration: underline">U</span>
        </button>,
      )}

      <button
        className={style.editor_control_button}
        onclick={() => format("<h1>")}
      >
        <b>
          H<sub>1</sub>
        </b>
      </button>
      <button
        className={style.editor_control_button}
        onclick={() => format("<h2>")}
      >
        <b>
          H<sub>2</sub>
        </b>
      </button>

      <button
        className={style.editor_control_button}
        onclick={() => apply("insertOrderedList")}
      >
        1.
      </button>
      <button
        className={style.editor_control_button}
        onclick={() => apply("insertUnorderedList")}
      >
        &#8226;
      </button>

      <button
        className={style.editor_control_button + style.text_with_icon}
        onclick={startMakingLink}
      >
        <i className={FA.icon_link_solid}></i>
        <div>{"Link"}</div>
      </button>

      <button
        className={style.editor_control_button + style.text_with_icon}
        onclick={startInsertingVariable}
      >
        <i className={FA.icon_hashtag_solid}></i>
        <div>{"Variabele"}</div>
      </button>

      {renderPopupModal(inputtingLinkS, ({ close }) => (
        <div style="padding: 32px 16px; width: 480px;">
          <article>{"Voeg een link rond de tekst toe"}</article>
          <div style="padding: 0px 8px">
            {textbox({
              source: linkInput,
              style: "margin-bottom: 0px",
              label: "Link",
            })}
          </div>
          <div style="padding: 8px; text-align: right">
            <button className={style.button_em_down} onclick={close}>
              {"Annuleer"}
            </button>
            <button onclick={saveLink}>{"OK"}</button>
          </div>
        </div>
      ))}

      {variables === null ? (
        <span></span>
      ) : (
        renderPopupModal(insertingVariableS, ({ close }) => {
          const form = new Form({})
            .addField("variable", [], async () =>
              radioField({
                initial: null,
                values: variables.variables,
                show: (s) => s.description,
                render: radioFieldCheckboxRenderInputLike,
                label: "Variabele",
              }),
            )
            .build();

          return (
            <div
              style="padding: 32px 16px; width: 480px;"
              className={style.panel}
            >
              <article>
                <b>{"Voeg een variabele toe"}</b>
              </article>
              <section>{form.render()}</section>

              <div style="padding: 8px; text-align: right">
                <button className={style.button_em_down} onclick={close}>
                  {"Annuleer"}
                </button>
                {renderSaveButton({
                  formS: form.s,
                  save: async ({ variable }) => {
                    insertVariable(variable);
                    close();
                    return null;
                  },
                  reload: false,
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return { buttonSection, editablediv };
}

function saveSelection(): null | Range[] {
  if (window.getSelection) {
    const sel = window.getSelection();
    if (sel === null) {
      return null;
    }
    if (sel.getRangeAt && sel.rangeCount) {
      const ranges = [];
      for (let i = 0, len = sel.rangeCount; i < len; ++i) {
        ranges.push(sel.getRangeAt(i));
      }
      return ranges;
    }
  }
  return null;
}
function restoreSelection(savedSel: Range[]) {
  if (savedSel) {
    const sel = window.getSelection();
    if (!sel) {
      return;
    }
    sel.removeAllRanges();
    for (let i = 0, len = savedSel.length; i < len; ++i) {
      sel.addRange(savedSel[i]);
    }
  }
}
function isValidSelection(sel: Range[]) {
  if (
    sel === null ||
    sel === undefined ||
    sel.length === 0 ||
    (sel[0] && sel[0].startOffset === sel[0].endOffset)
  ) {
    return false;
  } else {
    return true;
  }
}
